import { audienceLabel, type Agenda, type AgendaActivity, type Availability } from '@koderek/shared';
import { Alert, Avatar, Badge, Button, Group, SimpleGrid, Stack, Text, Title, Typography } from '@mantine/core';
import type { CSSProperties, ReactNode } from 'react';
import { Link } from 'react-router';
import { IconClock, IconMapPin, IconSchool, IconUsers } from '../../components/icons';
import { t } from '../../texts/pl';
import { formatTime } from '../editions/format';
import { EnrollmentAction } from '../enrollment/EnrollmentAction';
import type { EnrollmentControls } from '../enrollment/useEnrollment';
import classes from './ActivityDetail.module.css';
import { seats } from './agendaModel';

/** BR-31 w kafelku „Wolne miejsca”: „7 z 10”, po wyczerpaniu limitu „1 z 12 (rezerwa)”, na końcu „Brak miejsc”. */
function seatsText(a: AgendaActivity): string {
  if (a.enrolled < a.capacity) return t.agenda.seatsOf(a.capacity - a.enrolled, a.capacity);
  const { free } = seats(a);
  return free ? `${t.agenda.seatsOf(free, a.capacity + a.reserve)} (${t.agenda.fromReserve})` : t.agenda.full;
}

/** Kafelek informacji: ikona, podpis i wartość. */
function Fact({ icon, label, children }: { icon: ReactNode; label: string; children: ReactNode }) {
  return (
    <div className={classes.fact}>
      <span className={classes.factIcon}>{icon}</span>
      <div style={{ minWidth: 0 }}>
        <Text size="xs" c="dimmed" lh={1.2}>
          {label}
        </Text>
        <Text size="sm" fw={600} lh={1.3}>
          {children}
        </Text>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Stack gap={6}>
      <Title order={3} size="h5" className={classes.sectionTitle}>
        {title}
      </Title>
      {children}
    </Stack>
  );
}

/**
 * US-302: szczegóły zajęć (bez wymagań prowadzącego – te są tylko dla organizatorów, w panelu admina). Opis i bio są oczyszczane z niebezpiecznego HTML po stronie serwera przy zapisie (AC-3),
 * więc można je wyświetlić jako HTML.
 */
export function ActivityDetail({
  agenda,
  activity: a,
  loggedIn,
  controls = null,
  availability,
}: {
  agenda: Agenda;
  activity: AgendaActivity;
  loggedIn: boolean;
  controls?: EnrollmentControls | null;
  availability?: Availability;
}) {
  const live = availability?.items.find((i) => i.activityId === a.id);
  const type = agenda.types.find((x) => x.id === a.typeId);
  const room = agenda.rooms.find((x) => x.id === a.roomId);
  const here = `/edycje/${agenda.edition.slug}/zajecia/${encodeURIComponent(a.symbol)}`;
  const cancelled = a.status === 'cancelled';
  const seatColor = seats(a).free === 0 ? 'red' : a.enrolled >= a.capacity ? 'orange' : 'green';

  // US-501, US-502: zapis albo powód niedostępności dla wybranej osoby; gość – logowanie z powrotem tutaj (US-902 AC-2).
  const action = controls ? (
    <EnrollmentAction activity={a} controls={controls} live={live} opensAt={availability?.opensAt} />
  ) : a.status === 'active' && a.enrollment && !loggedIn ? (
    <Button component={Link} to={`/logowanie?powrot=${encodeURIComponent(here)}`} w="fit-content">
      {t.agenda.loginToEnroll}
    </Button>
  ) : (
    <EnrollmentAction activity={a} controls={null} live={live} opensAt={availability?.opensAt} />
  );

  return (
    <Stack gap="lg" className={classes.root} style={{ '--type-color': type?.color ?? 'var(--mantine-color-brand-6)' } as CSSProperties}>
      <Stack gap="xs" className={classes.header}>
        <Group gap={6}>
          {type ? (
            <Badge color={type.color} variant="light" tt="none">
              {type.name}
            </Badge>
          ) : null}
          <Badge color="gray" variant="outline" tt="none">
            {a.symbol}
          </Badge>
          {cancelled ? (
            <Badge color="red" variant="filled" tt="none">
              {t.agenda.cancelled}
            </Badge>
          ) : null}
        </Group>
        <Title order={2} size="h2" lh={1.2} td={cancelled ? 'line-through' : undefined}>
          {a.title}
        </Title>
        {a.speakers ? (
          <Text c="dimmed">
            {t.agenda.with} {a.speakers}
          </Text>
        ) : null}
      </Stack>

      {cancelled ? <Alert color="red">{t.agenda.cancelReason(a.cancelReason ?? '')}</Alert> : null}

      <SimpleGrid cols={{ base: 2, sm: a.enrollment ? 4 : 3 }} spacing="xs" verticalSpacing="xs">
        <Fact icon={<IconClock size={18} />} label={t.agenda.when}>
          {formatTime(a.startsAt)}–{formatTime(a.endsAt)}
        </Fact>
        <Fact icon={<IconMapPin size={18} />} label={t.agenda.room}>
          {room?.name ?? '—'}
        </Fact>
        <Fact icon={<IconSchool size={18} />} label={t.agenda.age}>
          {audienceLabel(a)}
        </Fact>
        {a.enrollment ? (
          <Fact icon={<IconUsers size={18} />} label={t.agenda.seatsLabel}>
            <Text span inherit c={cancelled ? undefined : `${seatColor}.8`}>
              {seatsText(a)}
            </Text>
          </Fact>
        ) : null}
      </SimpleGrid>

      <div className={classes.action}>{action}</div>

      {a.description ? (
        <Section title={t.agenda.about}>
          <Typography>
            <div dangerouslySetInnerHTML={{ __html: a.description }} />
          </Typography>
        </Section>
      ) : null}

      {a.speakerBios.length ? (
        <Section title={t.agenda.speakers}>
          <Stack gap="md">
            {a.speakerBios.map((s) => (
              <Group key={s.name} gap="sm" wrap="nowrap" align="flex-start">
                <Avatar radius="xl" color="brand" size={40}>
                  {s.name
                    .split(/\s+/)
                    .map((part) => part.slice(0, 1))
                    .join('')
                    .slice(0, 2)
                    .toUpperCase()}
                </Avatar>
                <Stack gap={2} miw={0}>
                  <Text fw={600}>{s.name}</Text>
                  <Typography fz="sm">
                    <div dangerouslySetInnerHTML={{ __html: s.bio }} />
                  </Typography>
                </Stack>
              </Group>
            ))}
          </Stack>
        </Section>
      ) : null}
    </Stack>
  );
}
